import type { GroupId, ViewModelAssetId, ViewModelId } from 'balus-common';
import { ViewModelFileStorageLimitBytes } from 'balus-common';
import { TRPCClientError } from '@trpc/client';
import { toast } from 'react-hot-toast';
import { t } from 'i18next';
import { trpcClient } from '@framework/trpcClient';
import type { CommandManager, ICommand } from '@model-framework/command';

type AssetOwner = { groupId: GroupId; viewModelId: ViewModelId };

const codeOf = (error: unknown): string | undefined =>
  error instanceof TRPCClientError ? error.shape?.data?.code : undefined;

// サーバーは URL 共有の匿名ユーザーを通常ユーザーではないとして BAD_REQUEST で拒否する
const isSignInRequired = (error: unknown): boolean => codeOf(error) === 'BAD_REQUEST';

const firstErrorOf = async (
  assetIds: ViewModelAssetId[],
  call: (assetId: ViewModelAssetId) => Promise<void>
): Promise<unknown> => {
  const results = await Promise.allSettled(assetIds.map(call));
  return results.find((result): result is PromiseRejectedResult => result.status === 'rejected')?.reason;
};

const moveToTrash = (owner: AssetOwner, assetId: ViewModelAssetId) =>
  trpcClient.viewModel.moveAssetToTrash.mutate({ ...owner, assetId });

const restoreFromTrash = (owner: AssetOwner, assetId: ViewModelAssetId) =>
  trpcClient.viewModel.restoreAssetFromTrash.mutate({ ...owner, assetId });

type AssetTransition = (owner: AssetOwner, assetIds: ViewModelAssetId[]) => Promise<string | null>;

// 失敗の応答が返っても、サーバーでは移動・復元が済んでいることがある（応答の喪失や、移動後の操作ログ保存の失敗）。
// 移動・復元はどちらも済んでいれば成功を返すため、失敗したら成否によらず全件を逆方向に戻して元の状態にそろえる。
const moveAllToTrash: AssetTransition = async (owner, assetIds) => {
  const error = await firstErrorOf(assetIds, (assetId) => moveToTrash(owner, assetId));
  if (!error) return null;

  console.error('ファイルをゴミ箱へ移せませんでした', error);
  const rollbackError = await firstErrorOf(assetIds, (assetId) => restoreFromTrash(owner, assetId));
  if (rollbackError) console.error('ゴミ箱へ移したファイルを戻せませんでした', rollbackError);
  return isSignInRequired(error) ? t('canvas.fileContainer.signInRequired') : t('canvas.fileContainer.delete.failed');
};

const restoreAllFromTrash: AssetTransition = async (owner, assetIds) => {
  const error = await firstErrorOf(assetIds, (assetId) => restoreFromTrash(owner, assetId));
  if (!error) return null;

  console.error('ファイルをゴミ箱から戻せませんでした', error);
  const rollbackError = await firstErrorOf(assetIds, (assetId) => moveToTrash(owner, assetId));
  if (rollbackError) console.error('戻したファイルをゴミ箱へ移し直せませんでした', rollbackError);
  if (codeOf(error) === 'PAYLOAD_TOO_LARGE') {
    return t('canvas.fileContainer.restore.overLimit', { limitGB: ViewModelFileStorageLimitBytes / 1024 ** 3 });
  }
  return isSignInRequired(error) ? t('canvas.fileContainer.signInRequired') : t('canvas.fileContainer.restore.failed');
};

// ゴミ箱への移動を待つ間に同じ要素の削除が重ねて実行されると、同じ要素の削除コマンドが履歴に二重に積まれ、
// 片方を undo した後にもう片方が undo できず履歴が詰まる。移動中の実体を含む削除は受け付けない。
// 別ビューへの移動も同じ実体を指すカードを作るため、移動中の実体を含むときは別ビューへ移さない。
const assetIdsBeingDeleted = new Set<ViewModelAssetId>();

export const isFileContainerAssetBeingDeleted = (assetId: ViewModelAssetId): boolean =>
  assetIdsBeingDeleted.has(assetId);

export class FileContainerAssetTrashCommand implements ICommand {
  private constructor(
    private readonly owner: AssetOwner,
    private readonly assetIds: ViewModelAssetId[],
    private readonly command: ICommand,
    private readonly beforeUndo: AssetTransition,
    private readonly beforeRedo: AssetTransition
  ) {}

  static async executeDeletion(
    commandManager: CommandManager,
    owner: AssetOwner,
    assetIds: ViewModelAssetId[],
    command: ICommand
  ): Promise<boolean> {
    const uniqueAssetIds = Array.from(new Set(assetIds));
    if (uniqueAssetIds.length === 0) {
      commandManager.execute(command);
      return true;
    }
    if (uniqueAssetIds.some((assetId) => assetIdsBeingDeleted.has(assetId))) return false;

    uniqueAssetIds.forEach((assetId) => assetIdsBeingDeleted.add(assetId));
    try {
      // 移動に失敗した削除を undo の履歴に積むと、要素が残っているため undo できないコマンドが履歴の先頭に残り、
      // それより前の操作も undo できなくなる。そのため初回の移動は履歴に積む前に済ませる。
      const errorMessage = await moveAllToTrash(owner, uniqueAssetIds);
      if (errorMessage) {
        toast.error(errorMessage);
        return false;
      }

      commandManager.execute(
        new FileContainerAssetTrashCommand(owner, uniqueAssetIds, command, restoreAllFromTrash, moveAllToTrash)
      );
      return true;
    } finally {
      uniqueAssetIds.forEach((assetId) => assetIdsBeingDeleted.delete(assetId));
    }
  }

  static executeCreation(
    commandManager: CommandManager,
    owner: AssetOwner,
    assetIds: ViewModelAssetId[],
    command: ICommand
  ): void {
    const uniqueAssetIds = Array.from(new Set(assetIds));
    commandManager.execute(
      uniqueAssetIds.length === 0
        ? command
        : new FileContainerAssetTrashCommand(owner, uniqueAssetIds, command, moveAllToTrash, restoreAllFromTrash)
    );
  }

  do(): void {
    this.command.do();
  }

  undo(): void {
    this.beforeUndo(this.owner, this.assetIds).then((errorMessage) => {
      if (errorMessage) {
        toast.error(errorMessage);
        return;
      }
      this.command.undo();
    });
  }

  redo(): void {
    this.beforeRedo(this.owner, this.assetIds).then((errorMessage) => {
      if (errorMessage) {
        toast.error(errorMessage);
        return;
      }
      this.command.redo();
    });
  }

  canUndo(): Promise<boolean> {
    return this.command.canUndo();
  }

  canRedo(): Promise<boolean> {
    return this.command.canRedo();
  }
}
